import React, { useRef, useEffect, useState } from 'react';
import './window.css';
import { soundService } from '../services/SoundService.ts';

interface WindowProps {
  onClose: () => void;
  children: React.ReactNode;
  windowType?: string;
  onClick?: () => void;
  isActive?: boolean;
  title?: string;
  icon?: string;
}

const Window: React.FC<WindowProps> = ({ children, onClose, windowType, onClick, isActive = false, title = '', icon }) => {
  const windowRef = useRef<HTMLDivElement>(null);
  const [windowPosition, setWindowPosition] = useState<{left: string, top: string}>({ left: '50%', top: '50%' });
  const [isPositioned, setIsPositioned] = useState(false);

  useEffect(() => {
    soundService.init();
  }, []);

  const getWindowSize = () => {
    if (windowType === 'recycle_bin' || windowType === 'log-password' || windowType === 'updatefail') {
      return {
        width: 'clamp(225px, calc(100vw * 2/3), 360px)',
        height: 'clamp(120px, calc(100vw * 0.89/3), 160px)',
        minHeight: '120px',
        maxHeight: '80vh'
      };
    }
    if (windowType === 'update') {
      return {
        width: 'clamp(290px, calc(100vw * 2/3), 500px)',
        height: 'clamp(200px, calc(100vw * 1.5/3), 330px)',
        minHeight: '180px',
        maxHeight: '80vh'
      };
    }
    if (windowType === 'camera') {
      return {
        width: 'clamp(300px, calc(100vw * 2/3), 500px)',
        height: 'clamp(270px, calc(100vw * 1.8/3), 450px)',
        minHeight: '180px',
        maxHeight: '80vh'
      };
    }
    if (windowType === 'log-contents') {
      return {
        width: 'clamp(300px, calc(60vw * 2/3), 750px)',
        height: 'clamp(300px, calc(60vw * 1.8/3), 750px)',
      };
    }
    if (windowType === 'file-viewer-txt') {
      return {
        width: 'clamp(300px, calc(100vw * 2/3), 450px)',
        height: 'clamp(200px, calc(100vw * 1.8/3), 280px)',
      };
    }
    if (windowType === 'file-viewer-pdf') {
      return {
        width: 'clamp(300px, calc(60vw * 2/3), 600px)',
        height: 'clamp(420px, calc(60vw * 2.83/3), 850px)',
        minHeight: '300px',
        maxHeight: '80vh'
      };
    }
    
    return {
      width: 'min(calc(100vw * 2/3), 800px)',
      height: 'min(calc(100vh * 9/20), 360px)'
    };
  };

  useEffect(() => {
    if (isPositioned) return;

    const windowElem = windowRef.current;
    if (!windowElem) return;
    
    const rect = windowElem.getBoundingClientRect();
    const left = (window.innerWidth - rect.width) / 2;
    const top = (window.innerHeight - rect.height) / 2;
    
    const isFileViewer = windowType === 'file-viewer-txt' || windowType === 'file-viewer-pdf';
    const horizontalOffset = isFileViewer ? window.innerWidth * 0.05 : 0;
    const verticalOffset = isFileViewer ? -window.innerHeight * 0.05 : 0;
    
    setWindowPosition({
      left: `${left + horizontalOffset}px`,
      top: `${top + verticalOffset}px`
    });
    setIsPositioned(true);
  }, [isPositioned, windowType]);

  const onMouseDown = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();

    const windowElem = windowRef.current;
    if (!windowElem) return;

    const shiftX = e.clientX - windowElem.getBoundingClientRect().left;
    const shiftY = e.clientY - windowElem.getBoundingClientRect().top;

    const moveAt = (pageX: number, pageY: number) => {
      windowElem.style.left = pageX - shiftX + 'px';
      windowElem.style.top = pageY - shiftY + 'px';
    };

    const onMouseMove = (e: MouseEvent) => {
      moveAt(e.pageX, e.pageY);
    };

    const onTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      const touch = e.touches[0];
      moveAt(touch.pageX, touch.pageY);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('touchmove', onTouchMove, { passive: false });

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('touchend', onMouseUp);
    };

    document.addEventListener('mouseup', onMouseUp);
    document.addEventListener('touchend', onMouseUp);
  };

  const onTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    e.preventDefault();
    const windowElem = windowRef.current;
    if (!windowElem) return;

    const touch = e.touches[0];
    const shiftX = touch.clientX - windowElem.getBoundingClientRect().left;
    const shiftY = touch.clientY - windowElem.getBoundingClientRect().top;

    const moveAt = (pageX: number, pageY: number) => {
      windowElem.style.left = pageX - shiftX + 'px';
      windowElem.style.top = pageY - shiftY + 'px';
    };

    const onTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      const touch = e.touches[0];
      moveAt(touch.pageX, touch.pageY);
    };

    document.addEventListener('touchmove', onTouchMove, { passive: false });

    const onTouchEnd = () => {
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
    };

    document.addEventListener('touchend', onTouchEnd);
  };

  const handleClick = () => {
    soundService.playClickSound();
    if (onClick) onClick();
  };

  const handleCloseClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    soundService.playClickSound();
    onClose();
  };

  const { width, height } = getWindowSize();
  
  return (
    <div
      ref={windowRef}
      className={`window ${isActive ? 'active' : ''}`}
      style={{
        width,
        height,
        left: windowPosition.left,
        top: windowPosition.top,
        visibility: isPositioned ? 'visible' : 'hidden'
      }}
      onClick={handleClick}
      data-window-type={windowType}
    >
      <div 
        className="window-header"
        onMouseDown={(e) => {
          const target = e.target as HTMLElement;
          if (target.classList.contains('close-button') || target.parentElement?.classList.contains('close-button')) {
            return;
          }
          onMouseDown(e);
        }}
        onTouchStart={(e) => {
          const target = e.target as HTMLElement;
          if (target.classList.contains('close-button') || target.parentElement?.classList.contains('close-button')) {
            return;
          }
          onTouchStart(e);
        }}
      >
        <img src={icon} alt="Window Icon" className="window-icon" />
        <span className="window-title">{title}</span>
        <button
          className="close-button"
          onClick={handleCloseClick}
        >
          <span className="close-button-x">×</span>
        </button>
      </div>
      <div className="window-content">
        {children}
      </div>
    </div>
  );
};

export default Window;